<script lang="ts">
import { Flipped } from '@monogrid/vue-boost/flip-toolkit'
import { templateRef } from '@vueuse/core'
import type { ComponentPublicInstance, PropType } from 'vue'
import { defineComponent, watch } from 'vue'

import LayerLinearGrid from '@/components/molecules/LayerLinearGrid.vue'
import type { GetProjectQuery } from '@/graphql'
import { getFullYear } from '@/utils'

export default defineComponent({
  name: 'ProjectHeroText',
  components: {
    Flipped,
    LayerLinearGrid
  },
  props: {
    content: { type: Object as PropType<GetProjectQuery['content']>, required: true },
    flipped: { type: Boolean, default: false },
    revealable: { type: Boolean, default: true }
  },
  emits: ['flip-complete'],
  setup (props, { attrs, slots, emit }) {
    const background = templateRef<ComponentPublicInstance<typeof LayerLinearGrid>>('background')

    watch(background, value => value?.hide({
      duration: 1,
      delay: 0.25,
      onComplete: () => emit('flip-complete')
    }))

    return {
      getFullYear
    }
  }
})
</script>

<template>
  <Flipped
    :flip-id="`project-hero-text-${content._slug}`"
    class="relative container pt-safe-header pb-6 lg:pb-8 h-full w-full flex flex-col justify-between gap-4"
  >
    <LayerLinearGrid v-if="flipped" ref="background" class="absolute inset-0 c-blue" default-visible />
    <div><slot name="header" /></div>
    <div v-reveal.group="revealable ? undefined : 'none'" class="w-full relative lg:flex lg:items-end lg:justify-between">
      <div class="lg:w-[60%]">
        <h1 v-reveal.child class="h3 mb-6 mix-blend-exclusion">{{ content.title }}</h1>
        <div v-reveal.child v-reveal:start-at="0.1" class="c-grey flex flex-wrap gap-3 lg:gap-4 lg:gap-12">
          <p class="h5 lg:min-w-30">
            {{ '{'+getFullYear(content.date)+'}' }}
          </p>
          <p
            v-for="category in content.categories"
            :key="category.id"
            class="h5"
            primary
          >
            {{ category.title }}
          </p>
        </div>
      </div>
      <div v-if="$slots.default" class="relative overflow-hidden lt-lg:min-h-16 lt-lg:pt-10">
        <div v-reveal.child="'scaleX'" class="border-horizontal lg:hidden origin-left bg-grey-dark top-5" />
        <div v-reveal.child="'slideUp'" v-reveal:start-at="0.1 * (content.categories.length + 3)">
          <slot />
        </div>
      </div>
    </div>
  </Flipped>
</template>
